/**
 * Первый экран доступа. Без нажатия браузер ничего не разрешит: ни две вкладки,
 * ни звук, ни уведомление, ни скачивание расширения. Поэтому это кнопка, а не
 * тихий запрос при загрузке.
 *
 * Сайт не умеет поставить расширение сам — магазин так больше не работает.
 * Тем же жестом качаем архив и сразу проверяем, что вкладки, звук, уведомление
 * и мостик живы. Человек должен увидеть результат, а не просто закрытый диалог.
 */

import { useEffect, useState } from 'react';

import { notifyTest } from './alarm';
import { ExtWarn } from './ExtWarn';
import {
  PROXY_DOWNLOAD_URL,
  proxySupported,
  EXT_MIN,
  EXT_SILENT,
  arm,
  extFresh,
  extSeen,
  glance,
  hearExt,
  inspect,
  markDone,
  missing,
  passed,
  extZipUrl,
  storeUrl,
  type Check,
} from './permit';

export function Permit({ onDone }: { onDone: () => void }): React.ReactElement {
  const [blocked, setBlocked] = useState(false);
  const [result, setResult] = useState<Check | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = (): void => {
    markDone();
    onDone();
  };

  const go = (): void => {
    const popups = arm();
    setBlocked(!popups);
    setResult(glance(popups));
    setBusy(true);
    void inspect(popups).then(
      (one) => {
        setResult(one);
        setBusy(false);
      },
      () => setBusy(false),
    );
  };

  useEffect(() => {
    if (result === null) return;
    if (extFresh(result.ext) && result.notify) return;
    const tick = (): void => {
      const current = extSeen();
      if (current !== null && current !== result.ext) {
        setResult((prev) => (prev === null ? prev : { ...prev, ext: current }));
      }
      if (result.ext === null) {
        void hearExt().then((version) => {
          if (version === null) return;
          setResult((prev) => (prev === null ? prev : { ...prev, ext: version }));
        });
      }
      try {
        if (
          !result.notify &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        ) {
          setResult((prev) => {
            if (prev === null || prev.notify) return prev;
            return { ...prev, notify: notifyTest() };
          });
        }
      } catch {
        // Нет Notification — строка так и останется красной.
      }
    };
    const id = window.setInterval(tick, 1000);
    const onShow = (): void => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onShow);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, [result]);

  const ok = result !== null && passed(result);
  const lack = result === null ? [] : missing(result);
  const store = storeUrl();
  const windows = proxySupported();

  return (
    <div className="nx-permit" role="dialog" aria-label="доступ браузера">
      <div className="nx-permit__card">
        <b>{ok ? 'браузер настроен' : 'настроим рабочее пространство'}</b>
        {result === null ? (
          <p>
            {blocked
              ? 'браузер закрыл вторую вкладку. нажмите значок всплывающих окон в адресной строке → разрешить для этого сайта, затем снова «разрешить и проверить».'
              : 'чтобы открывать обе конторы сразу, слышать новую вилку и сверять кэф на странице, браузеру нужны всплывающие окна, звук, уведомления и наше расширение. для AdsPower также подготовим FORQ Proxy.'}
          </p>
        ) : (
          <>
            <ul className="nx-permit__list">
              <Row
                ok={result.popups}
                name="всплывающие окна"
                yes="две вкладки открылись"
                no="браузер закрыл вторую"
              />
              <Row ok={result.sound} name="звук" yes="тон прозвучал" no="не слышно" />
              <Row ok={result.notify} name="уведомления" yes="проверочное ушло" no="не разрешены" />
              <Row
                ok={extFresh(result.ext)}
                name="расширение"
                yes={extFresh(result.ext) ? `на связи · ${result.ext}` : ''}
                no={result.ext === null ? 'не отвечает' : `стоит ${result.ext}, нужна ${EXT_MIN}`}
              />
            </ul>
            {!extFresh(result.ext) && (
              <div className="nx-permit__how">
                {store !== null && result.ext === null ? (
                  'в соседней вкладке откройте карточку и нажмите «добавить». вернитесь сюда — строка станет зелёной сама.'
                ) : result.ext === null ? (
                  <>
                    {EXT_SILENT}{' '}
                    <button
                      type="button"
                      className="nx-permit__dl"
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        cursor: 'pointer',
                      }}
                      onClick={() => window.location.reload()}
                    >
                      обновить вкладку
                    </button>
                    . первый раз:{' '}
                    <a className="nx-permit__dl" href={extZipUrl()} download="forq-extension.zip">
                      скачать архив
                    </a>{' '}
                    → chrome://extensions → режим разработчика → загрузить распакованное. карточку
                    не удалять.
                  </>
                ) : (
                  <>
                    <ExtWarn />
                  </>
                )}
              </div>
            )}
            {blocked && (
              <p className="nx-permit__how">
                значок всплывающих окон — в адресной строке. разрешите для этого сайта и нажмите
                «повторить».
              </p>
            )}
            {!ok && lack.length > 0 && (
              <p className="nx-permit__lack">не хватает: {lack.join(', ')}</p>
            )}
          </>
        )}
        <div className="nx-permit__proxy">
          <strong>
            FORQ Proxy <span>для AdsPower</span>
          </strong>
          {windows ? (
            <>
              <p>
                {result === null
                  ? 'вместе с расширением скачается приложение. открой установщик, войди через Telegram и подключи профиль AdsPower.'
                  : 'мы запросили скачивание установщика. открой его из загрузок браузера. если файл не появился — скачай кнопкой ниже.'}
              </p>
              <a
                className="nx-permit__dl"
                href={PROXY_DOWNLOAD_URL}
                download="FORQ-Proxy-Setup.exe"
              >
                скачать FORQ Proxy · Windows
              </a>
              <small>
                после установки обновления приходят автоматически. скачивание не означает, что
                приложение уже установлено.
              </small>
            </>
          ) : (
            <p>
              FORQ Proxy пока доступен для Windows 10/11 x64. браузер можно настроить и продолжить
              без него.
            </p>
          )}
        </div>
        <button
          type="button"
          className="nx-permit__go"
          onClick={ok ? finish : go}
          disabled={busy && !ok}
        >
          {ok ? 'дальше' : result === null ? 'разрешить и проверить' : 'повторить'}
        </button>
        <button type="button" className="nx-permit__skip" onClick={finish}>
          пока пропустить
        </button>
      </div>
    </div>
  );
}

function Row({
  ok,
  name,
  yes,
  no,
}: {
  ok: boolean;
  name: string;
  yes: string;
  no: string;
}): React.ReactElement {
  return (
    <li className={ok ? 'nx-permit__row nx-permit__row--ok' : 'nx-permit__row nx-permit__row--no'}>
      <span>{name}</span>
      <em>{ok ? yes : no}</em>
    </li>
  );
}
